import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';

interface WalletContextType {
  // Wallet state
  isConnected: boolean;
  address: string | null;
  chainId: string | null;
  balance: string | null;
  
  // Connection methods
  connectWallet: () => Promise<void>;
  disconnectWallet: () => void;
  
  // Multi-chain support
  switchChain: (chainId: string) => Promise<void>;
  supportedChains: ChainInfo[];
  
  // Loading states
  isLoading: boolean;
}

interface ChainInfo {
  id: string;
  name: string;
  icon: string;
  isSupported: boolean;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

const SUPPORTED_CHAINS: ChainInfo[] = [
  {
    id: 'polygon',
    name: 'Polygon',
    icon: '🔷',
    isSupported: true,
  },
  {
    id: 'xrpl',
    name: 'XRPL',
    icon: '⚡',
    isSupported: true,
  },
  {
    id: 'solana',
    name: 'Solana',
    icon: '☀️',
    isSupported: true,
  },
];

export const WalletProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [address, setAddress] = useState<string | null>(null);
  const [chainId, setChainId] = useState<string | null>(null);
  const [balance, setBalance] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const connectWallet = async () => {
    setIsLoading(true);
    try {
      if (typeof window.ethereum !== 'undefined') {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await provider.send('eth_requestAccounts', []);
        
        if (accounts.length > 0) {
          const account = accounts[0];
          const network = await provider.getNetwork();
          
          setAddress(account);
          setChainId(network.chainId.toString());
          setIsConnected(true);
          
          // Get balance
          const balance = await provider.getBalance(account);
          setBalance(ethers.formatEther(balance));
          
          toast.success('Wallet connected successfully!');
        }
      } else {
        toast.error('Please install MetaMask to use LegacyLayer');
      }
    } catch (error) {
      console.error('Error connecting wallet:', error);
      toast.error('Failed to connect wallet');
    } finally {
      setIsLoading(false);
    }
  };

  const disconnectWallet = () => {
    setIsConnected(false);
    setAddress(null);
    setChainId(null);
    setBalance(null);
    toast.success('Wallet disconnected');
  };

  const switchChain = async (newChainId: string) => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: newChainId }],
        });
        setChainId(newChainId);
        toast.success('Chain switched successfully');
      }
    } catch (error) {
      console.error('Error switching chain:', error);
      toast.error('Failed to switch chain');
    }
  };

  // Check if wallet is already connected on mount
  useEffect(() => {
    const checkWalletConnection = async () => {
      if (typeof window.ethereum !== 'undefined') {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await provider.listAccounts();
        
        if (accounts.length > 0) {
          const account = accounts[0];
          const network = await provider.getNetwork();
          
          setAddress(account.address);
          setChainId(network.chainId.toString());
          setIsConnected(true);
          
          const balance = await provider.getBalance(account.address);
          setBalance(ethers.formatEther(balance));
        }
      }
    };

    checkWalletConnection();
  }, []);

  const value: WalletContextType = {
    isConnected,
    address,
    chainId,
    balance,
    connectWallet,
    disconnectWallet,
    switchChain,
    supportedChains: SUPPORTED_CHAINS,
    isLoading,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}; 